/**
 * InAppLiveCapture (S4-live) — LIVE face + liveness capture INSIDE the Telegram
 * Mini App webview. Props: { ctx, api, tg, nav, state, setState,
 * params:{direction, branch_id?, geo} }.
 *
 * WHY THIS EXISTS (researched + confirmed): getUserMedia DOES work in the
 * Telegram webview on iOS as long as the <video> is autoplay + playsinline +
 * MUTED (the black-preview is a WebView autoplay policy, not Telegram blocking
 * the camera — ionic pwa-elements #135). So the whole capture can run in-app on
 * iOS. Android getUserMedia is flaky/often denied → we feature-detect and, on
 * any failure or a dead (no-frame) stream, fall back to the existing browser
 * hand-off (startBrowserPunch → openLink /capture). It NEVER dead-ends.
 *
 * ── TWO-PHASE CAPTURE (once the face is PLACED, i.e. inside the GREEN oval) ──
 *   Oval is RED while not placed, GREEN when placed.
 *   PHASE 1 — PROMPT: a random action cue is shown big (Jilmayng / Chap
 *     ko'zingizni yuming / O'ng ko'zingizni yuming / Qimirlamang=be-still). The
 *     worker performs it. For a GESTURE we require a LIVE low→high TRANSITION of
 *     its blendshape (a static photo that starts already-smiling never dips low
 *     → rejected); for 'still' we require a neutral face held with the nose
 *     stable across the window (and record whether natural micro-motion was
 *     seen). When the action holds for STABLE_FRAMES we STASH that exact video
 *     frame (bestFrameRef) and advance to phase 2. `livenessOkRef` carries the
 *     on-device verdict → sent as `liveness_ok` (advisory; the server still runs
 *     the real match + anti-spoof).
 *   PHASE 2 — COUNTDOWN: an explicit 4→3→2→1 (starts at 4s). Each tick shows the
 *     big number, fires tg.haptic('light'), and a PROGRESS-RING arc sweeps
 *     around the oval (drawn in paint()). Losing placement mid-countdown aborts
 *     back to phase 1. At 0 → capture the STASHED best frame (else the current
 *     frame), downscaled to ~720px / jpeg 0.8, and punch.
 *
 *   Has initData in-app → api.checkin/checkout directly (NO token) with
 *   {..., action, liveness_ok}. FACE_MISMATCH / LIVENESS_FAIL → retry, bumping
 *   state.faceFails; ≥3 → manager.
 *
 * Cleanup on unmount: stop the stream, cancel the rAF, close the landmarker.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import Screen from '../components/Screen.jsx';
import Button from '../components/Button.jsx';
import Spinner from '../components/Spinner.jsx';
import ErrorState from '../components/ErrorState.jsx';
import { T, placementText, actionText } from '../lib/i18n.js';
import { SCREENS } from '../App.jsx';
import { startBrowserPunch } from '../lib/handoff.js';
import {
  createFaceTracker,
  loadDrawing,
  facePlacement,
  matchesAction,
  actionSignal,
  isNeutral,
  noseXY,
  randomAction,
  OVAL,
  LIVENESS_LOW,
  STILL_MAX_DEV,
  STILL_MICRO_MIN,
} from '../lib/headpose.js';

// ---- tuning -----------------------------------------------------------------
const NO_FRAME_MS = 6000;    // no video pixels within this → show cam-fail reason
const STABLE_FRAMES = 5;     // action must hold this many frames before phase 2
const COUNTDOWN_MS = 4000;   // phase-2 countdown length (shows 4→3→2→1)
const HARD_CAP_MS = 15000;   // snap anyway after this (never dead-end)
const MAX_EDGE = 720;        // downscale so the POST body stays small
const JPEG_Q = 0.8;
const MESH_COLOR = 'rgba(120,200,255,0.28)';
const MESH_WIDTH = 1;

// Face-match / liveness fails that escalate to a manager after 3 tries.
const FACE_FAILS = new Set(['FACE_MISMATCH', 'LIVENESS_FAIL']);
// Errors that mean "your GPS/branch was wrong" — recover by re-doing LOCATION.
const LOCATION_ERRORS = new Set([
  'OUT_OF_RANGE', 'NO_LOCATION', 'ACCURACY_MISSING', 'ACCURACY_TOO_LOW',
  'BAD_RANGE', 'NO_BRANCH', 'BRANCH_NOT_ALLOWED', 'BRANCH_NO_COORDS',
]);

export default function InAppLiveCapture({ ctx, api, tg, nav, state, setState, params }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const direction = params?.direction === 'out' ? 'out' : 'in';
  const geo = params?.geo || state.geo || {};
  const branchId = params?.branch_id ?? state.branchId ?? undefined;
  const branchName =
    ctx.branches.find((b) => b.id === branchId)?.name ||
    ctx.branches[0]?.name ||
    '';

  // phase drives the render; the rAF loop reads refs, never state.
  const [phase, setPhase] = useState('init'); // init | live | sending | error | camfail | fallback
  const [status, setStatus] = useState(T.live_starting_cam);
  const [guide, setGuide] = useState('');      // big guidance / action prompt line
  const [camDbg, setCamDbg] = useState(null);  // {gum, play} ms — dev timing, shown small
  const [count, setCount] = useState(0);       // countdown number shown in phase 2
  const [errCode, setErrCode] = useState(null);
  const [camReason, setCamReason] = useState(''); // why in-app camera failed
  const [forcedRecover, setForcedRecover] = useState(null);
  const [attempt, setAttempt] = useState(0);   // bump → re-run the mount effect

  // Long-lived refs.
  const aliveRef = useRef(true);
  const streamRef = useRef(null);
  const landmarkerRef = useRef(null);
  const drawRef = useRef(null);                // { drawingUtils, tesselation }
  const rafRef = useRef(0);
  const capturedRef = useRef(false);
  const trackStartRef = useRef(0);
  const firstFrameRef = useRef(false);

  // ── two-phase state (all in refs; the loop mutates them each frame) ──────────
  const livePhaseRef = useRef('prompt');       // 'prompt' (phase 1) | 'count' (phase 2)
  const actionRef = useRef(null);              // chosen action cue once placed
  const heldRef = useRef(0);                   // consecutive frames the action held
  const bestFrameRef = useRef(null);           // stashed <canvas> of the action frame
  const countStartRef = useRef(0);             // perf.now when phase-2 countdown began
  const lastTickRef = useRef(0);               // last integer tick emitted (per-tick haptic)
  // liveness A3 — gesture: require a low→high transition; still: micro-motion.
  const sawLowRef = useRef(false);             // gesture signal dipped below LIVENESS_LOW
  const movedRef = useRef(false);              // micro-motion seen during a 'still' hold
  const stillHistRef = useRef([]);             // recent nose positions for 'still'
  const livenessOkRef = useRef(false);         // advisory on-device liveness verdict

  // ---- teardown -------------------------------------------------------------
  const stopAll = useCallback(() => {
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = 0; }
    const s = streamRef.current;
    if (s) { try { s.getTracks().forEach((t) => t.stop()); } catch (_e) { /**/ } }
    streamRef.current = null;
    const lm = landmarkerRef.current;
    if (lm) { try { lm.close(); } catch (_e) { /**/ } }
    landmarkerRef.current = null;
    drawRef.current = null;
  }, []);

  // ---- automatic browser hand-off fallback (Android / camera fail) ----------
  const toBrowserFallback = useCallback(async () => {
    if (!aliveRef.current || phase === 'fallback') return;
    stopAll();
    setPhase('fallback');
    setStatus(T.live_opening_browser);
    // Re-mint a token + openLink the /capture page (getUserMedia works there).
    const res = await startBrowserPunch({ direction, branch_id: branchId, tg });
    if (!aliveRef.current) return;
    if (!res || !res.ok) {
      tg.haptic('error');
      setErrCode((res && res.error) || 'SERVER_ERROR');
      setForcedRecover(null);
      setPhase('error');
    }
    // On success the worker finishes in the browser and returns to Telegram;
    // this screen just waits on the fallback splash.
  }, [phase, stopAll, direction, branchId, tg]);

  const fail = useCallback((code, forced) => {
    if (!aliveRef.current) return;
    stopAll();
    tg.haptic('error');
    setErrCode(code || 'SERVER_ERROR');
    setForcedRecover(forced || null);
    setPhase('error');
  }, [stopAll, tg]);

  // In-app camera couldn't open / produce frames — show WHY + a MANUAL browser
  // option instead of silently relocating, so we can diagnose per device.
  const camFail = useCallback((reason) => {
    if (!aliveRef.current) return;
    stopAll();
    setCamReason(reason || '');
    setPhase('camfail');
  }, [stopAll]);

  // ---- capture the still and punch -----------------------------------------
  const captureAndPunch = useCallback(async () => {
    if (capturedRef.current || !aliveRef.current) return;
    capturedRef.current = true;
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = 0; }
    tg.haptic('success');
    setPhase('sending');
    setStatus(T.live_sending);

    // Prefer the STASHED best frame (the exact frame the action happened on);
    // fall back to the current live frame (hard-cap / no stash path).
    const v = videoRef.current;
    const src = bestFrameRef.current || v;
    const sw = (src && (src.videoWidth || src.width)) || 0;
    const sh = (src && (src.videoHeight || src.height)) || 0;
    if (!sw || !sh) { fail('MISSING_IMAGE'); return; }

    const scale = Math.min(1, MAX_EDGE / Math.max(sw, sh));
    const cw = Math.round(sw * scale);
    const ch = Math.round(sh * scale);
    const off = document.createElement('canvas');
    off.width = cw; off.height = ch;
    // RAW (un-mirrored) frame — matches the enrolment still the sidecar expects.
    // drawImage accepts either a <video> or a <canvas> source.
    try { off.getContext('2d').drawImage(src, 0, 0, cw, ch); }
    catch (_e) { fail('MISSING_IMAGE'); return; }

    let image_b64;
    try {
      const url = off.toDataURL('image/jpeg', JPEG_Q);
      image_b64 = url.slice(url.indexOf(',') + 1);
    } catch (_e) { fail('MISSING_IMAGE'); return; }

    // Release the camera light before the (blocking) round-trip.
    stopAll();

    const payload = {
      lat: geo.lat,
      lng: geo.lng,
      accuracy: geo.accuracy,
      branch_id: branchId,
      image_b64,
      action: actionRef.current || undefined,
      liveness_ok: livenessOkRef.current,
    };
    const res = direction === 'in'
      ? await api.checkin(payload)
      : await api.checkout(payload);

    if (!aliveRef.current) return;
    if (res && res.ok) {
      tg.haptic('success');
      const time = direction === 'in' ? res.check_in_time : res.check_out_time;
      const result = { direction, time, branch: branchName };
      setState({ result });
      nav.replace(SCREENS.RESULT, result);
    } else {
      const code = (res && res.error) || 'SERVER_ERROR';
      if (FACE_FAILS.has(code)) {
        const fails = (state.faceFails || 0) + 1;
        setState({ faceFails: fails });
        fail(code, fails >= 3 ? 'manager' : undefined);
      } else {
        fail(code);
      }
    }
  }, [api, tg, nav, setState, state, geo, branchId, branchName, direction, fail, stopAll]);

  // ---- phase helpers (mutate refs, called from the loop) --------------------
  // Reset back to PHASE 1 — on placement loss or a fresh placement.
  const resetToPrompt = useCallback(() => {
    livePhaseRef.current = 'prompt';
    actionRef.current = null;
    heldRef.current = 0;
    bestFrameRef.current = null;
    countStartRef.current = 0;
    lastTickRef.current = 0;
    sawLowRef.current = false;
    movedRef.current = false;
    stillHistRef.current = [];
    livenessOkRef.current = false;
    setCount(0);
  }, []);

  // Stash the exact current video frame (raw, native res) as the "best frame".
  const stashBestFrame = useCallback(() => {
    const v = videoRef.current;
    const vw = v?.videoWidth || 0;
    const vh = v?.videoHeight || 0;
    if (!vw || !vh) return;
    const c = document.createElement('canvas');
    c.width = vw; c.height = vh;
    try { c.getContext('2d').drawImage(v, 0, 0, vw, vh); bestFrameRef.current = c; }
    catch (_e) { /* keep bestFrameRef null → capture falls back to live frame */ }
  }, []);

  // PHASE 1 → PHASE 2: stash the action frame, start the 4→3→2→1 countdown.
  const advanceToCount = useCallback((livenessOk) => {
    stashBestFrame();
    livenessOkRef.current = !!livenessOk;
    livePhaseRef.current = 'count';
    countStartRef.current = performance.now();
    lastTickRef.current = 999; // force the first tick(4) to differ → haptic fires
    setCount(4);
    setGuide(T.live_ready);
    setStatus(T.live_hold);
    tg.haptic('medium');
  }, [stashBestFrame, tg]);

  // ---- overlay drawing ------------------------------------------------------
  // Draw the video frame (belt-and-suspenders) + mesh (mirrored to match the
  // selfie preview), then the oval ring on top; in phase 2 a bright progress
  // arc (0..1) sweeps around the oval as the countdown elapses.
  function paint(canvas, video, landmarks, placed, progress) {
    const vw = video.videoWidth || 0;
    const vh = video.videoHeight || 0;
    if (!vw || !vh) return;
    if (canvas.width !== vw || canvas.height !== vh) {
      canvas.width = vw; canvas.height = vh;
    }
    const c = canvas.getContext('2d');
    c.save();
    // Mirror horizontally: video frame + mesh both flip, so the mesh sits on the
    // face exactly as the worker sees themselves.
    c.translate(vw, 0); c.scale(-1, 1);
    try { c.drawImage(video, 0, 0, vw, vh); } catch (_e) { /**/ }
    const kit = drawRef.current;
    if (landmarks && kit && kit.drawingUtils) {
      try {
        kit.drawingUtils.drawConnectors(landmarks, kit.tesselation, {
          color: MESH_COLOR, lineWidth: MESH_WIDTH,
        });
      } catch (_e) { /**/ }
    }
    c.restore();
    // Oval ring — symmetric, so mirror doesn't matter; draw un-transformed.
    const ex = OVAL.cx * vw;
    const ey = OVAL.cy * vh;
    const rx = OVAL.rx * vw;
    const ry = OVAL.ry * vh;
    c.beginPath();
    c.ellipse(ex, ey, rx, ry, 0, 0, Math.PI * 2);
    c.lineWidth = Math.max(3, vw * 0.008);
    c.strokeStyle = placed ? 'rgba(46,204,113,0.95)' : 'rgba(231,76,60,0.9)';
    c.setLineDash(placed ? [] : [Math.max(6, vw * 0.02), Math.max(6, vw * 0.02)]);
    c.stroke();
    c.setLineDash([]);
    // Phase-2 progress ring — a bright arc sweeping clockwise from 12 o'clock.
    if (progress != null) {
      const start = -Math.PI / 2;
      c.beginPath();
      c.ellipse(ex, ey, rx, ry, 0, start, start + Math.max(0, Math.min(1, progress)) * Math.PI * 2);
      c.lineWidth = Math.max(5, vw * 0.013);
      c.lineCap = 'round';
      c.strokeStyle = 'rgba(46,204,113,1)';
      c.stroke();
      c.lineCap = 'butt';
    }
  }

  // ---- per-frame loop -------------------------------------------------------
  const loop = useCallback(() => {
    if (capturedRef.current || !aliveRef.current) return;
    const v = videoRef.current;
    const canvas = canvasRef.current;
    const lm = landmarkerRef.current;
    const now = performance.now();

    if (v && canvas && v.readyState >= 2 && (v.videoWidth || 0) > 0) {
      firstFrameRef.current = true;
      if (!lm) {
        // Face model still downloading → paint the live preview NOW (raw video +
        // red locating oval) instead of a black canvas. Detection, mesh and the
        // countdown stay off until landmarkerRef resolves; the loop reads it
        // every frame and picks it up automatically. This removes the ~2-4s of
        // black screen the worker used to see while the WASM+model loaded.
        paint(canvas, v, null, false, null);
      } else {
      let det = null;
      try { det = lm.detectForVideo(v, now); } catch (_e) { det = null; }
      const marks = det && det.faceLandmarks && det.faceLandmarks[0];
      const shapes = det && det.faceBlendshapes && det.faceBlendshapes[0]
        ? det.faceBlendshapes[0].categories : null;

      const place = facePlacement(marks);
      const inCount = livePhaseRef.current === 'count';
      const progress = inCount && countStartRef.current
        ? (now - countStartRef.current) / COUNTDOWN_MS
        : null;
      paint(canvas, v, marks, place.placed, place.placed ? progress : null);

      if (!place.placed) {
        // Lost placement — abort any countdown and reset to phase 1.
        if (livePhaseRef.current !== 'prompt' || actionRef.current) resetToPrompt();
        setGuide(placementText(place.reason));
      } else if (livePhaseRef.current === 'count') {
        // ── PHASE 2 — COUNTDOWN ──
        const tick = Math.ceil((COUNTDOWN_MS - (now - countStartRef.current)) / 1000);
        if (tick !== lastTickRef.current && tick > 0) {
          lastTickRef.current = tick;
          setCount(tick);
          tg.haptic('light');
        }
        setGuide(T.live_ready);
        setStatus(T.live_hold);
        if (now - countStartRef.current >= COUNTDOWN_MS) {
          captureAndPunch();
          return;
        }
      } else {
        // ── PHASE 1 — PROMPT ──
        if (!actionRef.current) {
          actionRef.current = randomAction();
          heldRef.current = 0;
          sawLowRef.current = false;
          movedRef.current = false;
          stillHistRef.current = [];
        }
        const action = actionRef.current;
        setGuide(actionText(action));
        setStatus(T.live_hold);

        if (action === 'still') {
          // 'still' = neutral face + nose stable across the window; record
          // whether any natural micro-motion occurred (liveness for a photo=0).
          const n = noseXY(marks);
          if (!n || !isNeutral(shapes)) {
            heldRef.current = 0;
            stillHistRef.current = [];
          } else {
            const hist = stillHistRef.current;
            hist.push(n);
            if (hist.length > STABLE_FRAMES) hist.shift();
            if (hist.length >= STABLE_FRAMES) {
              let mx = 0, my = 0;
              for (const p of hist) { mx += p.x; my += p.y; }
              mx /= hist.length; my /= hist.length;
              let maxDev = 0;
              for (const p of hist) {
                const d = Math.hypot(p.x - mx, p.y - my);
                if (d > maxDev) maxDev = d;
                if (d > STILL_MICRO_MIN) movedRef.current = true;
              }
              if (maxDev < STILL_MAX_DEV) {
                advanceToCount(movedRef.current);
                rafRef.current = requestAnimationFrame(loop); // keep looping into the countdown
                return;
              }
              heldRef.current = 0; // moved too much — keep waiting
            }
          }
        } else {
          // GESTURE — require a LIVE low→high transition, not a static high.
          const sig = actionSignal(action, shapes);
          if (sig < LIVENESS_LOW) sawLowRef.current = true;
          if (matchesAction(action, shapes)) {
            heldRef.current += 1;
            // Only advance once we've seen the signal dip low first (a held-up
            // photo that starts already-high never dips → stays here until the
            // hard-cap safety net, so it can't game the gesture).
            if (heldRef.current >= STABLE_FRAMES && sawLowRef.current) {
              advanceToCount(true);
              rafRef.current = requestAnimationFrame(loop); // keep looping into the countdown
              return;
            }
            if (heldRef.current > STABLE_FRAMES) heldRef.current = STABLE_FRAMES;
          } else {
            heldRef.current = 0;
          }
        }
      }
      } // end else — landmarker ready (detection + countdown branch)
    }

    // No frames within NO_FRAME_MS → show the reason (readyState/size) instead
    // of silently bouncing to the browser, so we can see WHY on this device.
    if (!firstFrameRef.current && now - trackStartRef.current > NO_FRAME_MS) {
      const vv = videoRef.current;
      camFail('Kamera kadr bermadi — readyState=' + (vv ? vv.readyState : '?') +
        ', ' + (vv ? (vv.videoWidth + 'x' + vv.videoHeight) : '?'));
      return;
    }
    // Hard safety net: never hang. Snap the stashed/current frame. Gated on the
    // landmarker so the preview-only window (model still loading) can never
    // auto-capture without a liveness check.
    if (landmarkerRef.current && firstFrameRef.current && now - trackStartRef.current > HARD_CAP_MS) {
      captureAndPunch();
      return;
    }
    rafRef.current = requestAnimationFrame(loop);
  }, [captureAndPunch, camFail, tg, resetToPrompt, advanceToCount]);

  // ---- one-shot setup on mount (re-runs on `attempt`) -----------------------
  useEffect(() => {
    aliveRef.current = true;
    capturedRef.current = false;
    firstFrameRef.current = false;
    resetToPrompt();
    let cancelled = false;

    (async () => {
      // 1) camera — feature-detect, then getUserMedia ONCE.
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        camFail('getUserMedia bu webviewda mavjud emas'); return;
      }
      setPhase('init');
      setStatus(T.live_starting_cam);
      const t0 = performance.now();
      let stream;
      try {
        // 640x480, not 1280x960. The face landmarker downsamples its input, so
        // HD buys nothing for detection — but on the iOS Telegram webview a
        // high-res request makes the camera negotiate a slower capture mode,
        // and getUserMedia + the first frame is exactly the "3+ seconds to
        // start" the worker feels. The v1 change (decoupling the MediaPipe
        // load from the preview) could not help while THIS was the real wait:
        // the preview still blocks on getUserMedia + play. Lower res returns
        // the stream and the first frame markedly sooner.
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
          audio: false,
        });
      } catch (e) {
        // Denied / no camera / webview refusal → show the exact error.
        if (!cancelled) {
          camFail('getUserMedia: ' + ((e && e.name) || 'XATO') +
            ((e && e.message) ? (' — ' + e.message) : ''));
        }
        return;
      }
      if (cancelled || !aliveRef.current) {
        try { stream.getTracks().forEach((t) => t.stop()); } catch (_e) { /**/ }
        return;
      }
      const tCam = performance.now();
      streamRef.current = stream;
      const v = videoRef.current;
      if (!v) { camFail('video element topilmadi'); return; }
      v.srcObject = stream;
      v.setAttribute('playsinline', '');
      v.muted = true;
      try { await v.play(); } catch (_e) { /* autoplay quirk — frames still flow */ }
      // Surface the split ON SCREEN so the next phone test ends the guessing
      // with a screenshot instead of a remote debugger: a big getUserMedia
      // means the camera negotiation itself, a big play means the first frame.
      // Rendered small at the bottom; harmless if it stays.
      setCamDbg({
        gum: Math.round(tCam - t0),
        play: Math.round(performance.now() - tCam),
      });

      // 2) Show the LIVE PREVIEW immediately. Start the render loop now — it
      // paints the raw camera frames onto the canvas right away. Previously the
      // loop only started AFTER the face model finished loading (an 11 MB WASM +
      // 3.7 MB .task download, ~2-4s), so the worker stared at a black screen the
      // whole time. The model now loads in the BACKGROUND (step 3) and the loop
      // begins detecting the moment landmarkerRef is set. trackStartRef is set
      // here so NO_FRAME_MS measures "frames since play", not since model-ready.
      setPhase('live');
      setGuide(T.live_show_face);
      trackStartRef.current = performance.now();
      rafRef.current = requestAnimationFrame(loop);

      // 3) landmarker (blendshapes) + drawing kit — in parallel, in the
      // background. The preview is already running; nothing here blocks it.
      setStatus(T.live_loading_face);
      let tracker; let kit;
      try {
        [tracker, kit] = await Promise.all([
          createFaceTracker({ blendshapes: true }),
          loadDrawing(),
        ]);
      } catch (_e) {
        if (!cancelled) fail('FACE_SERVICE_DOWN');
        return;
      }
      if (cancelled || !aliveRef.current) {
        try { tracker.close(); } catch (_e) { /**/ }
        return;
      }
      const canvas = canvasRef.current;
      drawRef.current = {
        drawingUtils: canvas ? new kit.DrawingUtils(canvas.getContext('2d')) : null,
        tesselation: kit.FACE_LANDMARKS_TESSELATION,
      };
      // Set the landmarker LAST — the loop starts detecting (and drawing the
      // mesh) the instant this is non-null, so drawRef must already be ready.
      landmarkerRef.current = tracker;
    })();

    return () => {
      cancelled = true;
      aliveRef.current = false;
      stopAll();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt]);

  function restart() {
    setErrCode(null);
    setForcedRecover(null);
    setCount(0);
    setGuide('');
    setAttempt((a) => a + 1);
  }

  // ---- render ---------------------------------------------------------------
  if (phase === 'camfail') {
    return (
      <Screen title={T.title_face}>
        <div style={{ padding: 8 }}>
          <div style={{ fontSize: 40, textAlign: 'center' }}>📷</div>
          <div style={{ fontWeight: 700, fontSize: 16, textAlign: 'center', margin: '8px 0' }}>
            Kamera ilova ichida ochilmadi
          </div>
          <div style={{
            fontSize: 12.5, fontFamily: 'ui-monospace, monospace', color: 'var(--ink-2)',
            background: 'rgba(127,127,127,0.12)', padding: 10, borderRadius: 8,
            wordBreak: 'break-word', textAlign: 'center',
          }}>{camReason || '—'}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 14 }}>
            <Button onClick={() => toBrowserFallback()}>📲 Brauzerda davom etish</Button>
            <Button variant="ghost" onClick={restart}>🔄 Qayta urinish</Button>
          </div>
        </div>
      </Screen>
    );
  }

  if (phase === 'error') {
    return (
      <Screen title={T.title_face}>
        <ErrorState
          code={errCode}
          recover={forcedRecover || undefined}
          onRecover={(verb) => {
            if (verb === 'manager' || verb === 'home') nav.reboot();
            else if (LOCATION_ERRORS.has(errCode)) nav.pop(); // back to LOCATION
            else restart();
          }}
        />
      </Screen>
    );
  }

  const showGuide = phase === 'live' && guide;
  return (
    <div className="cap">
      <div className="cap__video-wrap">
        {/* Muted+playsinline+autoplay is the iOS black-preview fix. The canvas
            on top repaints the frame each rAF, so even a black <video> shows. */}
        <video ref={videoRef} className="cap__video" playsInline autoPlay muted />
        <canvas ref={canvasRef} className="cap__canvas" />

        {showGuide ? (
          <div className="cap__cue">
            <div className="cue cue-big">{guide}</div>
            {count > 0 ? <div className="cue-arrow" aria-hidden="true">{count}</div> : null}
          </div>
        ) : null}

        <div className="cap__status">
          {phase === 'sending' || phase === 'fallback' || phase === 'init' ? (
            <Spinner size="md" inline />
          ) : null}
          <div>{status}</div>
          {camDbg ? (
            <div style={{ fontSize: '10px', opacity: 0.5, marginTop: '2px' }}>
              kamera {camDbg.gum}ms · kadr {camDbg.play}ms
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
